import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LangProvider } from "@/lib/i18n";
import AppLayout from "@/components/app/AppLayout";
import LoginPage from "@/pages/LoginPage";
import ClientLookupPage from "@/pages/ClientLookupPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import RateServicePage from "@/pages/RateServicePage";
import DashboardPage from "@/pages/DashboardPage";
import NewComplaintPage from "@/pages/NewComplaintPage";
import ComplaintDetailPage from "@/pages/ComplaintDetailPage";
import UsersPage from "@/pages/UsersPage";
import DriverDetailPage from "@/pages/DriverDetailPage";
import DriverSanctionsPage from "@/pages/DriverSanctionsPage";
import ServicesPage from "@/pages/ServicesPage";
import NewServicePage from "@/pages/NewServicePage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import DriverDashboardPage from "@/pages/DriverDashboardPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import NewAnnouncementPage from "@/pages/NewAnnouncementPage";
import AnnouncementDetailPage from "@/pages/AnnouncementDetailPage";
import ChatPage from "@/pages/ChatPage";
import HomePage from "@/pages/HomePage";
import PricingPage from "@/pages/PricingPage";
import UniversalTranslatorPage from "@/pages/UniversalTranslatorPage";
import ProfilePage from "@/pages/ProfilePage";
import MarketingPage from "@/pages/MarketingPage";
import NotesPage from "@/pages/NotesPage";
import CalendarPage from "@/pages/CalendarPage";
import FormsPage from "@/pages/FormsPage";
import BankFormPage from "@/pages/BankFormPage";
import { canUseMarketing } from "@/lib/permissions";
import "@/App.css";

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/services" replace />;
  return children;
}

function StaffRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  return children;
}

function MarketingRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!canUseMarketing(user)) return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <HomePage />;
}

export default function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/consulta" element={<ClientLookupPage />} />
          <Route path="/reservar" element={<PublicBookingPage />} />
          <Route path="/formulario/:token" element={<BankFormPage />} />
          <Route path="/calificar" element={<RateServicePage />} />

          <Route element={<AppLayout />}>
            <Route path="/" element={<RoleHome />} />

            {/* Driver */}
            <Route path="/driver" element={<DriverDashboardPage />} />
            <Route path="/perfil" element={<ProfilePage />} />
            <Route path="/notas" element={<StaffRoute><NotesPage /></StaffRoute>} />
            <Route path="/formularios" element={<AdminRoute><FormsPage /></AdminRoute>} />
            <Route path="/calendario" element={<StaffRoute><CalendarPage /></StaffRoute>} />
            <Route path="/marketing" element={<MarketingRoute><MarketingPage /></MarketingRoute>} />

            {/* Services (staff + drivers for detail) */}
            <Route
              path="/services"
              element={
                <StaffRoute>
                  <ServicesPage />
                </StaffRoute>
              }
            />
            <Route
              path="/services/new"
              element={
                <StaffRoute>
                  <NewServicePage />
                </StaffRoute>
              }
            />
            <Route
              path="/services/historial"
              element={
                <StaffRoute>
                  <ServicesPage historyMode />
                </StaffRoute>
              }
            />
            <Route path="/services/:id" element={<ServiceDetailPage />} />

            {/* Announcements */}
            <Route path="/announcements" element={<AnnouncementsPage />} />
            <Route
              path="/announcements/new"
              element={
                <AdminRoute>
                  <NewAnnouncementPage />
                </AdminRoute>
              }
            />
            <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />

            {/* Chat */}
            <Route path="/chat" element={<ChatPage />} />

            <Route
              path="/traductor"
              element={
                <StaffRoute>
                  <UniversalTranslatorPage />
                </StaffRoute>
              }
            />

            {/* Complaints (staff only) */}
            <Route
              path="/dashboard"
              element={
                <StaffRoute>
                  <DashboardPage />
                </StaffRoute>
              }
            />
            <Route
              path="/complaints"
              element={
                <StaffRoute>
                  <DashboardPage />
                </StaffRoute>
              }
            />
            <Route
              path="/complaints/new"
              element={
                <StaffRoute>
                  <NewComplaintPage />
                </StaffRoute>
              }
            />
            <Route
              path="/complaints/:id"
              element={
                <StaffRoute>
                  <ComplaintDetailPage />
                </StaffRoute>
              }
            />

            <Route
              path="/users"
              element={
                <AdminRoute>
                  <UsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/drivers/:id"
              element={
                <AdminRoute>
                  <DriverDetailPage />
                </AdminRoute>
              }
            />
            <Route
              path="/drivers/:id/amonestaciones"
              element={
                <AdminRoute>
                  <DriverSanctionsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/precios"
              element={
                <AdminRoute>
                  <PricingPage />
                </AdminRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </LangProvider>
    </AuthProvider>
  );
}
